import { DefaultButton, PrimaryButton, ActionButton } from '@fluentui/react/lib/Button';
import '../App.sass';
import { Text } from '@fluentui/react/lib/Text';
import Section from '../components/Section';
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { authenticationAtom } from '../State';
import { useState, useRef, useEffect } from 'react';
import Dialog, { DialogType, DialogFooter } from '@fluentui/react/lib/Dialog';
import { PasswordChangePanel } from '../components/PasswordChangePanel';
import { TextField } from '@fluentui/react/lib/TextField';
import { isPossiblePhoneNumber } from 'libphonenumber-js';
import { useAdministrationAPI } from '../AdministrationAPI';
import { useModalContext } from '../components/ModalProvider';
import { Stack } from '@fluentui/react/lib/Stack';
import { setupAuthenticatorApp, verifyAuthenticatorApp, disableMFA } from '../UserAPI';
import AuthCode from 'react-auth-code-input';
import PhoneInput from '../components/PhoneInput';

interface Properties {
}

export default function SecurityPanel(properties: Properties) {
    const { t } = useTranslation();
    const { pushError } = useModalContext() as any;
    const { updateUser } = useAdministrationAPI();
    const [authentication, setAuthentication] = useAtom(authenticationAtom) as any;
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [mfaDialogOpen, setMFADialogOpen] = useState(false);
    const [password, setPassword] = useState(false);
    const [isPasswordValid, setPasswordValid] = useState(false);
    const [mobileNumber, setMobileNumber] = useState();
    const [editedMobileNumber, setEditedMobileNumber] = useState();
    const [authAppDialogOpen, setAuthAppDialogOpen] = useState(false);
    const [authAppEnabled, setAuthAppEnabled] = useState(authentication.authenticatorAppEnabled);
    const [authAppStep, setAuthAppStep] = useState<'setup' | 'verify' | 'done'>('setup');
    const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
    const [authAppSecret, setAuthAppSecret] = useState<string | null>(null);
    const [verificationCode, setVerificationCode] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [verifyError, setVerifyError] = useState<string | null>(null);

    const togglePasswordDialogOpen = () => {
        setPasswordDialogOpen(!passwordDialogOpen);
    }
    const toggleMFADialogOpen = () => {
        setEditedMobileNumber(authentication.mobileNumber);
        setMFADialogOpen(!mfaDialogOpen);
    }
    const toggleAuthAppDialogOpen = () => setAuthAppDialogOpen(open => !open);
    const isPhoneNumberValid = (): boolean => {
        return isPossiblePhoneNumber(editedMobileNumber || '');
    }

    const openAuthAppDialog = () => {
        setAuthAppDialogOpen(true);
        setAuthAppStep('setup');
        setQrCodeUrl(null);
        setAuthAppSecret(null);
        setVerificationCode('');
        setVerifyError(null);
    };

    async function startAuthAppSetup() {
        // Call backend to get QR code and secret
        try {
            const data = await setupAuthenticatorApp();
            setQrCodeUrl(data.qrCodeUrl);
            setAuthAppSecret(data.secret);
            setAuthAppStep('verify');
        } catch (e) {
            setVerifyError('Failed to start setup. Please try again.');
        }
    }

    async function verifyAuthAppCode(code: string) {
        setVerifying(true);
        setVerifyError(null);
        try {
            const data = await verifyAuthenticatorApp(code, authAppSecret!);
            if (data.success) {
                setAuthAppEnabled(true);
                setAuthAppStep('done');
            } else {
                setVerifyError('Invalid code. Please try again.');
            }
        } catch (e) {
            setVerifyError('Verification failed. Please try again.');
        }
        setVerifying(false);
    }

    const disableAuthApp = async () => {
        try {
            await disableMFA();
            setAuthAppEnabled(false);
            setAuthentication({ ...authentication, authenticatorAppEnabled: false });
        } catch (e) {
            pushError('Failed to disable authenticator app.');
        }
    };

    return <>
        <div className="displayFlex flexDirectionColumn gapL width600">
            <Section title={t('settingsPanel.securityPanel.password')}>
                <div className='displayFlex justifyContentEnd marginTopL'>
                    <DefaultButton text={t('global.change')} onClick={togglePasswordDialogOpen} />
                </div>
            </Section>
            <Section title={t('settingsPanel.securityPanel.mfa')}>
                <div className='displayFlex alignItemsCenter marginTopL'>
                    <Text className="fontWeightBold" variant='medium'>{t('settingsPanel.securityPanel.mobileNumber')}</Text>
                    <Text variant='medium'>{authentication.mobileNumber}</Text>
                    <div className='flexGrow1 displayFlex alignItemsCenter justifyContentEnd gapS'>
                        <DefaultButton text={t('global.change')} onClick={toggleMFADialogOpen} />
                    </div>
                </div>
                <div className="displayFlex alignItemsCenter gapS marginTopM">
                    <Text className="fontWeightBold" variant="medium">
                        {t('settingsPanel.securityPanel.authenticatorApp')}
                    </Text>
                    <Text variant="medium" style={{ color: authAppEnabled ? 'green' : 'gray' }}>
                        {authAppEnabled ? t('settingsPanel.securityPanel.enabled') : t('settingsPanel.securityPanel.notEnabled')}
                    </Text>
                    <div className='flexGrow1 displayFlex alignItemsCenter justifyContentEnd gapS'>
                        {authAppEnabled
                            ?
                            <DefaultButton text={t('global.disable')} onClick={disableAuthApp} /> :
                            <DefaultButton
                                text={authAppEnabled ? t('global.configure') : t('global.enable')}
                                onClick={openAuthAppDialog}
                            />
                        }
                    </div>
                </div>
            </Section>
        </div>
        <Dialog hidden={!passwordDialogOpen}
            onDismiss={togglePasswordDialogOpen}
            maxWidth={600}
            minWidth={600}
            dialogContentProps={{
                type: DialogType.normal,
                title: t('settingsPanel.securityPanel.changePassword'),
                //subText: 'Do you want to send this message without a subject?',
                //maxWidth: 800,
                //width: 800
            }}
            modalProps={{
                isBlocking: true,
            }}>
            <PasswordChangePanel setPassword={setPassword} setPasswordValid={setPasswordValid}></PasswordChangePanel>
            <DialogFooter className="marginTopXXL">
                <PrimaryButton text={t('global.change')} disabled={!isPasswordValid} onClick={() => {
                    togglePasswordDialogOpen();
                }} />
                <DefaultButton onClick={togglePasswordDialogOpen} text={t('global.cancel')} />
            </DialogFooter>
        </Dialog>
        <Dialog hidden={!mfaDialogOpen}
            onDismiss={toggleMFADialogOpen}
            maxWidth={600}
            minWidth={600}
            dialogContentProps={{
                type: DialogType.normal,
                title: t('settingsPanel.securityPanel.changeMobileNumber'),
            }}
            modalProps={{
                isBlocking: true,
            }}>
            <PhoneInput
                label={t('settingsPanel.securityPanel.mobileNumber')}
                value={editedMobileNumber}
                onChange={(value: string) => setEditedMobileNumber(value as any)}
                onValidationChange={() => {}}
                required
            />
            <DialogFooter className="marginTopXXL">
                <PrimaryButton disabled={!isPhoneNumberValid()} text={t('global.change')} onClick={async () => {
                    try {
                        const newAuthentication = { ...authentication, mobileNumber: editedMobileNumber };

                        await updateUser(newAuthentication.customer, newAuthentication);

                        setAuthentication(newAuthentication);
                        toggleMFADialogOpen();
                    } catch (error) {
                        pushError(error);
                    }
                }} />
                <DefaultButton onClick={toggleMFADialogOpen} text={t('global.cancel')} />
            </DialogFooter>
        </Dialog>
        <Dialog
            hidden={!authAppDialogOpen}
            onDismiss={() => setAuthAppDialogOpen(false)}
            maxWidth={600}
            minWidth={400}
            dialogContentProps={{
                type: DialogType.normal,
                title: t('settingsPanel.securityPanel.setupAuthenticatorApp'),
            }}
            modalProps={{
                isBlocking: true,
            }}
        >
            {authAppStep === 'setup' && (
                <>
                    <Text>{t('settingsPanel.securityPanel.authenticatorAppSetupInstructions')}</Text>
                    <PrimaryButton
                        className="marginTopL"
                        text={t('settingsPanel.securityPanel.showQr')}
                        onClick={startAuthAppSetup}
                    />
                </>
            )}
            {authAppStep === 'verify' && (
                <>
                    <Text>{t('settingsPanel.securityPanel.scanQr')}</Text>
                    {qrCodeUrl && (
                        <div style={{ textAlign: 'center', margin: '16px 0' }}>
                            <img src={qrCodeUrl} alt="Authenticator QR Code" style={{ width: 180, height: 180 }} />
                        </div>
                    )}
                    {authAppSecret && (
                        <div className="marginTopL">
                            <Text variant="medium" className="fontWeightBold">{t('settingsPanel.securityPanel.authenticatorKeyForRecovery')}</Text>
                            <TextField
                                readOnly
                                value={authAppSecret}
                                styles={{
                                    fieldGroup: [
                                        { 
                                            width: '100%',
                                            backgroundColor: '#f3f2f1'
                                        },
                                    ]
                                }}
                                onRenderInput={(props, defaultRender) => (
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        {defaultRender!(props)}
                                        <ActionButton
                                            iconProps={{ iconName: 'Copy' }}
                                            onClick={() => {
                                                navigator.clipboard.writeText(authAppSecret);
                                            }}
                                            title={t('global.copy')}
                                            styles={{
                                                root: { marginLeft: '8px' }
                                            }}
                                        />
                                    </div>
                                )}
                            />
                            <Text variant="small" style={{ color: '#666', marginTop: '4px' }}>
                                {t('settingsPanel.securityPanel.authenticatorKeyDescription')}
                            </Text>
                        </div>
                    )}
                    <div className="marginTopXL">
                        <Text variant="small" style={{ color: '#666', marginTop: '4px' }}>
                            {t('settingsPanel.securityPanel.enterAuthenticatorCodeDescription')}
                        </Text>
                    </div>
                    <div className="marginTopM displayFlex alignItemsCenter justifyContentCenter otp">
                        <AuthCode
                            onChange={(val: string) => {
                                setVerificationCode(val);
                                if (val.length === 6) {
                                    verifyAuthAppCode(val);
                                }
                            }}
                            allowedCharacters="numeric"
                            length={6}
                            containerClassName="marginBottomM"
                            inputClassName="auth-code-input"
                            autoFocus
                        />
                    </div>
                    {verifyError && <Text style={{ color: 'red' }}>{verifyError}</Text>}
                    <DialogFooter>
                        <PrimaryButton
                            text={t('settingsPanel.securityPanel.verify')}
                            onClick={() => verifyAuthAppCode(verificationCode)}
                            disabled={verificationCode.length !== 6 || verifying}
                        />
                        <DefaultButton onClick={() => setAuthAppDialogOpen(false)} text={t('global.cancel')} />
                    </DialogFooter>
                </>
            )}
            {authAppStep === 'done' && (
                <>
                    <Text>{t('settingsPanel.securityPanel.authenticatorAppEnabled')}</Text>
                    <DialogFooter>
                        <PrimaryButton
                            text={t('global.done')}
                            onClick={() => setAuthAppDialogOpen(false)}
                        />
                    </DialogFooter>
                </>
            )}
        </Dialog>
    </>;
}